import React, { useEffect, useState } from 'react';
import Isvg from 'react-inlinesvg';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';

import Button from 'components/Button';
import Callout from 'components/Callout';
import { Card } from 'components/Card';
import Message from 'components/Message';
import { openModal } from 'components/Modal';
import { Table } from 'components/Table';
import { TooltipContent } from 'components/Tooltip';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import { selectIsMobile } from 'selectors/ui';
import skuEmptyMenuIcon from 'static/illustrations/sku-empty-menu.svg';

import IntegratedPartnerBanner from 'features/Sku/Items/components/IntegratedPartnerBanner';
import { ITEM_AVAILABILITY_STATUS, PARTNER_STATE } from 'features/Sku/Items/SkuItems.constants';
import { isVisibleSkuEntity } from 'features/Sku/Items/SkuMenuData.normalizers';

import { DELETE_MODIFIER_GROUP_MODAL_NAME } from './components/DeleteModifierGroupModal';
import ModifierRow, { CELL_TOOLTIP } from './components/ModifierRow';
import { toDuplicateSeed } from './Modifiers.formState';
import useModifierGroups from './useModifierGroups';
import useModifierMutations from './useModifierMutations';
import useModifiersReadOnly from './useModifiersReadOnly';
import messages from './ModifierLibrary.messages';
import './ModifierLibrary.scss';

const { block, element } = bem('modifier-library');

const MODIFIER_LIBRARY_PAGE_SIZE = 20;

const ModifierLibrary = ({ venueId }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useSelector(selectIsMobile);
  const { isReadOnly, integrationName } = useModifiersReadOnly();
  const groupsQuery = useModifierGroups(venueId);
  const { setGroupEnabled, updateGroup } = useModifierMutations({ venueId });
  const [page, setPage] = useState(1);
  const [cellTip, setCellTip] = useState('');

  const groups = (groupsQuery.data ?? []).filter(isVisibleSkuEntity);
  // Deleting the last row of the last page shrinks the list under the stored page; clamping keeps
  // the view on the new last page instead of an empty one with no pager to escape through.
  const lastPage = Math.max(1, Math.ceil(groups.length / MODIFIER_LIBRARY_PAGE_SIZE));
  const currentPage = Math.min(page, lastPage);
  const pageGroups = groups.slice(
    (currentPage - 1) * MODIFIER_LIBRARY_PAGE_SIZE,
    currentPage * MODIFIER_LIBRARY_PAGE_SIZE
  );

  // react-tooltip only binds to data-tip elements it saw at build time; rows change with paging.
  useEffect(() => {
    ReactTooltip.rebuild();
  }, [groupsQuery.data, currentPage, isMobile]);

  const changeAvailability = group => status =>
    setGroupEnabled.mutate({
      groupId: group.core.id,
      enabled: status === ITEM_AVAILABILITY_STATUS.AVAILABLE,
    });

  const editGroup = group => navigate(makePath.skuModifierEdit({ venueId, groupId: group.core.id }));

  const duplicateGroup = group => {
    const copyName = intl.formatMessage(messages.copyName, { name: group.core.name });
    navigate(makePath.skuModifierAdd({ venueId }), { state: { seed: toDuplicateSeed(group, copyName) } });
  };

  const deleteGroup = group =>
    dispatch(
      openModal(DELETE_MODIFIER_GROUP_MODAL_NAME, {
        groupName: group.core.name,
        itemNames: (group.attachedItems || []).map(item => item.name),
        onConfirmDelete: () =>
          // The server refuses partner_state transitions on an enabled group.
          updateGroup.mutateAsync({
            groupId: group.core.id,
            group: { enabled: false, partnerState: PARTNER_STATE.DELETED },
          }),
      })
    );

  const header = (
    <tr>
      <th className={element('name-header')}>
        <Message message={messages.nameColumn} />
      </th>
      {!isMobile && (
        <th className={element('options-header')}>
          <Message message={messages.optionsColumn} />
        </th>
      )}
      {!isMobile && (
        <th className={element('used-in-header')}>
          <Message message={messages.usedInColumn} />
        </th>
      )}
      <th className={element('availability-header')}>
        <Message message={messages.availabilityColumn} />
      </th>
      {!isReadOnly && (
        <th className={element('actions-header')}>
          <span className={element('visually-hidden')}>
            <Message message={messages.actionsColumn} />
          </span>
        </th>
      )}
    </tr>
  );

  // The Table's loading and empty rows span colSpan={100}; under table-layout: fixed the browser
  // would size the table as 100 columns and crush the real headers, so fixed layout waits for rows.
  const hasRows = !groupsQuery.isLoading && pageGroups.length > 0;
  const isEmpty = !groupsQuery.isLoading && !groupsQuery.isError && groups.length === 0;
  const loadFailedWithNothingToShow = groupsQuery.isError && groups.length === 0;

  return (
    <div className={block({ loaded: hasRows })}>
      {isReadOnly && (
        <div className={element('integrated-banner')}>
          <IntegratedPartnerBanner integrationName={integrationName} />
        </div>
      )}

      <div className={element('header')}>
        <h3 className={element('heading')}>
          <Message message={messages.heading} />
        </h3>
        {!isReadOnly && (
          <Button
            styled="primary"
            className={element('new-button')}
            onClick={() => navigate(makePath.skuModifierAdd({ venueId }))}>
            <Isvg src={ICONS.add} className={element('new-icon')} />
            <Message message={messages.newModifier} />
          </Button>
        )}
      </div>

      {groupsQuery.isError && (
        <Callout className={element('load-error')} variant="error">
          <Message as="text" message={messages.loadError} />
        </Callout>
      )}

      {isEmpty && (
        <Card className={element('empty-card')}>
          <div className={element('empty-state')}>
            <img className={element('empty-icon')} src={skuEmptyMenuIcon} alt="" aria-hidden />
            <div className={element('empty-title')}>
              <Message message={messages.emptyTitle} />
            </div>
            {!isReadOnly && (
              <>
                <div className={element('empty-subtitle')}>
                  <Message message={messages.emptySubtitle} />
                </div>
                <Message
                  as={Button}
                  className={element('empty-action')}
                  message={messages.newModifier}
                  onClick={() => navigate(makePath.skuModifierAdd({ venueId }))}
                />
              </>
            )}
          </div>
        </Card>
      )}

      {!isEmpty && !loadFailedWithNothingToShow && (
        <Table
          className={element('table')}
          header={header}
          isLoading={groupsQuery.isLoading}
          emptyMessage={messages.emptyTitle}
          hasBottomPageTurner={groups.length > MODIFIER_LIBRARY_PAGE_SIZE}
          count={groups.length}
          entriesPerPage={MODIFIER_LIBRARY_PAGE_SIZE}
          page={currentPage}
          changePage={setPage}>
          {pageGroups.map(group => (
            <ModifierRow
              key={group.core.id}
              group={group}
              onCellHover={setCellTip}
              onChangeAvailability={changeAvailability(group)}
              onEdit={() => editGroup(group)}
              onDuplicate={() => duplicateGroup(group)}
              onDelete={() => deleteGroup(group)}
            />
          ))}
        </Table>
      )}

      <TooltipContent name={CELL_TOOLTIP} disable={!cellTip}>
        {cellTip}
      </TooltipContent>
    </div>
  );
};

ModifierLibrary.propTypes = {
  venueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default ModifierLibrary;
